import { useState } from "react";
import { Hexagon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreditPackageDto } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { rm, credits, fmtDate } from "../lib/format";
import { creditPackageImageUrl } from "../lib/credits";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { EmptyState, PageLoader } from "../components/ui";
import { PageHeader } from "../components/storefront/PageHeader";

function packSavingsPct(pack: CreditPackageDto): number | null {
  if (pack.compareAtPrice == null || pack.compareAtPrice <= pack.price) return null;
  return Math.round(((pack.compareAtPrice - pack.price) / pack.compareAtPrice) * 100);
}

function CreditPackTile({
  pack,
  buying,
  coinIcon,
  onBuy,
}: {
  pack: CreditPackageDto;
  buying: boolean;
  coinIcon: string | null;
  onBuy: () => void;
}) {
  const savings = packSavingsPct(pack);
  const onSale = savings != null;

  return (
    <button
      type="button"
      onClick={onBuy}
      disabled={buying}
      className="group relative flex flex-col items-center overflow-hidden rounded-2xl border border-line bg-surface/80 p-4 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all active:scale-[0.98] disabled:opacity-50 lg:hover:-translate-y-1 lg:hover:border-brand-pink/35 lg:hover:shadow-[0_12px_40px_rgba(221,84,235,0.12)]"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-brand-pink/12 via-brand-blue/5 to-transparent" />

      {onSale && (
        <span className="absolute right-2.5 top-2.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] font-semibold text-emerald-400 ring-1 ring-emerald-500/20">
          −{savings}%
        </span>
      )}

      <div className="relative mb-3 flex h-[4.5rem] w-full items-end justify-center">
        <div className="absolute bottom-1 h-10 w-16 rounded-full bg-brand-pink/25 blur-2xl transition-opacity group-hover:opacity-100 lg:opacity-70" />
        <img
          src={creditPackageImageUrl(coinIcon)}
          alt=""
          className="relative h-16 w-16 object-contain drop-shadow-[0_8px_24px_rgba(221,84,235,0.45)] transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      <p className="font-display text-xl font-bold leading-none text-gradient">{credits(pack.credits)}</p>
      <p className="mt-1.5 line-clamp-1 text-[10px] font-medium uppercase tracking-wide text-white/40">{pack.name}</p>

      <div className="mt-4 flex w-full flex-col items-center gap-0.5 border-t border-line/50 pt-3">
        {onSale && (
          <span className="text-[11px] text-white/35 line-through">{rm(pack.compareAtPrice!)}</span>
        )}
        <span className="font-display text-sm font-semibold text-white">{rm(pack.price)}</span>
        {onSale && (
          <span className="mt-1 text-[9px] font-medium text-emerald-400/90">Save {savings}%</span>
        )}
      </div>
    </button>
  );
}

interface WalletData {
  balance: number;
  transactions: {
    id: string;
    delta: number;
    balanceAfter: number;
    kind: string;
    note: string | null;
    createdAt: string;
  }[];
}

const KIND_LABEL: Record<string, string> = {
  TOPUP: "Top-up",
  SPEND: "Spent",
  REFUND: "Refund",
  ADJUSTMENT: "Adjustment",
  ADMIN_ADJUST: "Adjustment",
  TRADE: "Trade",
  REWARD: "Reward",
  BUYBACK: "Buyback",
  PRIZE: "Prize",
};

export function Wallet() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { config } = useAuth();
  const [buying, setBuying] = useState<string | null>(null);

  const { data: wallet, isLoading } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => api<WalletData>("/wallet"),
    enabled: Boolean(config?.creditsEnabled),
  });

  const { data: packs } = useQuery({
    queryKey: ["credit-packs"],
    queryFn: () => api<{ items: CreditPackageDto[] }>("/wallet/packages"),
    enabled: Boolean(config?.creditsEnabled),
  });

  if (!config?.creditsEnabled) {
    return (
      <div className="px-4 pt-10">
        <EmptyState icon={Hexagon} title="Credits are not enabled" hint="This store currently runs on direct payments only." />
      </div>
    );
  }

  if (isLoading) return <PageLoader />;

  async function buyPack(pack: CreditPackageDto) {
    setBuying(pack.id);
    try {
      await api("/cart/items", { body: { productId: pack.id, quantity: 1 } });
      await queryClient.invalidateQueries({ queryKey: ["cart"] });
      navigate("/checkout");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't start top-up");
    } finally {
      setBuying(null);
    }
  }

  return (
    <div className="space-y-5 px-4 pt-4 lg:px-6">
      <PageHeader
        title="Wallet"
        description="Top up credits for instant pack opens, raffle entries, and checkout."
      />

      {/* Balance card */}
      <div className="card-shine neon-ring relative overflow-hidden rounded-3xl bg-gradient-brand p-5 glow-brand">
        <p className="text-[11px] font-medium uppercase tracking-widest text-white/70">Credit balance</p>
        <p className="mt-1 font-display text-3xl font-bold">{credits(wallet?.balance ?? 0)}</p>
        <p className="mt-2 text-[10px] text-white/60">
          Use credits for instant pack opens, raffle entries and checkout.
        </p>
      </div>

      {/* Credit packs */}
      <section className="space-y-3">
        <div className="flex items-end justify-between gap-2">
          <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">Top up</h2>
          <p className="text-[10px] text-white/30">One pack per checkout</p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {packs?.items.map((pack) => (
            <CreditPackTile
              key={pack.id}
              pack={pack}
              buying={buying === pack.id}
              coinIcon={pack.imageUrl}
              onBuy={() => buyPack(pack)}
            />
          ))}
        </div>
        {!packs?.items.length && <p className="text-xs text-white/40">No credit packages configured yet.</p>}
      </section>

      {/* History */}
      <section className="space-y-2">
        <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">History</h2>
        {!wallet?.transactions.length ? (
          <p className="text-xs text-white/40">No transactions yet.</p>
        ) : (
          <div className="space-y-1.5">
            {wallet.transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between rounded-xl bg-surface/70 px-3.5 py-2.5">
                <div>
                  <p className="text-xs font-medium text-white/85">{tx.note ?? KIND_LABEL[tx.kind] ?? tx.kind}</p>
                  <p className="text-[10px] text-white/40">
                    {fmtDate(tx.createdAt)} · balance {tx.balanceAfter.toLocaleString()}
                  </p>
                </div>
                <span
                  className={`font-display text-sm font-bold ${tx.delta >= 0 ? "text-emerald-400" : "text-white/70"}`}
                >
                  {tx.delta >= 0 ? "+" : ""}
                  {tx.delta.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
